import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAppLogoUrl,
  getEventLogoUploadUrl,
  setEventLogo,
  deleteEventLogo,
} from '../../api/events';
import { uploadFileToS3 } from '../../api/photos';
import { RecordLogo } from '../common/RecordLogo';

const DEFAULT_LOGO_FALLBACK = '/logo.png';

export interface EventLogoProps {
  eventId: string;
  /** Resolved presigned URL of the event-specific logo (null if none). */
  eventLogoUrl: string | null;
  /** Show the hover edit overlay + clear button. Defaults to false (read-only). */
  editable?: boolean;
  /** Tailwind size class — defaults to `w-12 h-12`. */
  sizeClassName?: string;
  /** Used by tests to access the file input without clicking the overlay. */
  inputId?: string;
}

/**
 * Shows the event's logo. Falls back to the app-default logo (resolved from
 * `app_logo_s3_key`) and ultimately to a bundled brand asset. When `editable`,
 * hovering reveals an edit pencil that opens a file picker; the upload goes
 * directly to S3 via a presigned URL, then the s3Key is attached to the event.
 */
export function EventLogo({
  eventId,
  eventLogoUrl,
  editable = false,
  sizeClassName = 'w-12 h-12',
  inputId,
}: EventLogoProps) {
  const queryClient = useQueryClient();

  // Resolve the app-default logo lazily — only when no event-specific logo.
  const { data: appLogoUrl } = useQuery({
    queryKey: ['app-logo-url'],
    queryFn: getAppLogoUrl,
    enabled: !eventLogoUrl,
    staleTime: 60_000,
  });

  return (
    <RecordLogo
      imageUrl={eventLogoUrl ?? appLogoUrl ?? null}
      fallback={DEFAULT_LOGO_FALLBACK}
      label="event logo"
      hasOwnLogo={!!eventLogoUrl}
      editable={editable}
      upload={async (file) => {
        const { uploadUrl, s3Key } = await getEventLogoUploadUrl(eventId, file.name, file.type);
        await uploadFileToS3(uploadUrl, file);
        return setEventLogo(eventId, s3Key);
      }}
      clear={() => deleteEventLogo(eventId)}
      onChanged={() => queryClient.invalidateQueries({ queryKey: ['events'] })}
      onExpired={() => queryClient.invalidateQueries({ queryKey: ['events'] })}
      sizeClassName={sizeClassName}
      inputId={inputId}
      inputTestId="event-logo-file-input"
    />
  );
}
